import type { ComponentType, SVGProps } from 'react';
import React, { useRef, useState } from 'react';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react';

import { ZIndex } from '@rover/constants/src/styles/ZIndex';
import Loading from '@rover/icons/svg/loading.svg';
import MenuMoreAlt from '@rover/icons/svg/menu-more-alt.svg';
import { Box, Flex } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';

export type ActionMenuItem = {
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  onSelect: () => void;
  destructive?: boolean;
};

export type Props = {
  items: ActionMenuItem[];
  triggerLabel: string;
  minWidth?: string;
  onOpenChange?: (isOpen: boolean) => void;
  isBusy?: boolean;
  'data-testid'?: string;
};

const MENU_OFFSET = 8;
const TRIGGER_SIZE = '36px';
const ICON_SIZE = 16;

const ActionMenu = ({
  items,
  triggerLabel,
  minWidth = '170px',
  onOpenChange,
  isBusy = false,
  'data-testid': dataTestId,
}: Props): JSX.Element => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (nextIsOpen: boolean): void => {
    if (nextIsOpen && isBusy) return;

    setIsOpen(nextIsOpen);
    onOpenChange?.(nextIsOpen);
  };
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const itemRefs = useRef<Array<HTMLElement | null>>([]);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: handleOpenChange,
    placement: 'bottom-end',
    whileElementsMounted: autoUpdate,
    middleware: [offset(MENU_OFFSET), flip(), shift({ padding: MENU_OFFSET })],
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    useClick(context),
    useDismiss(context),
    useRole(context, { role: 'menu' }),
    useListNavigation(context, {
      listRef: itemRefs,
      activeIndex,
      onNavigate: setActiveIndex,
      loop: true,
    }),
  ]);

  itemRefs.current.length = items.length;

  const select = (item: ActionMenuItem): void => {
    handleOpenChange(false);
    // If we simply call item.onSelect() the focus remains in the image grid
    // instead of moving to the next focusable element (like an opened modal)
    setTimeout(item.onSelect, 0);
  };

  return (
    <>
      <Flex
        webAs="button"
        ref={refs.setReference}
        {...getReferenceProps({
          type: 'button',
          'aria-label': triggerLabel,
          'aria-disabled': isBusy,
          'aria-busy': isBusy,
        })}
        data-testid={dataTestId}
        width={TRIGGER_SIZE}
        height={TRIGGER_SIZE}
        padding="0x"
        borderRadius="round"
        alignItems="center"
        justifyContent="center"
        sx={{
          border: `${theme.borderWidth.primary} solid ${theme.background.interactive.primary_active}`,
          backgroundColor: theme.background.interactive.primary_base,
          color: theme.textColor.secondary,
          boxShadow: theme.shadows.elevation_low,
          cursor: isBusy ? 'progress' : 'pointer',
          ':hover': {
            backgroundColor: isBusy
              ? theme.background.interactive.primary_base
              : theme.background.interactive.primary_pressed,
          },
        }}
      >
        {isBusy ? (
          <Loading width={ICON_SIZE} height={ICON_SIZE} />
        ) : (
          <MenuMoreAlt width={ICON_SIZE} height={ICON_SIZE} fill="currentColor" />
        )}
      </Flex>
      {isOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <Box
              ref={refs.setFloating}
              {...getFloatingProps()}
              padding="1x"
              borderRadius="primary"
              minWidth={minWidth}
              background="primary"
              sx={{
                ...floatingStyles,
                boxShadow: theme.shadows.elevation_low,
                zIndex: ZIndex.POPOVER.toString(),
              }}
            >
              {items.map((item, index) => (
                <Flex
                  key={item.label}
                  webAs="button"
                  ref={(node: HTMLElement | null) => {
                    itemRefs.current[index] = node;
                  }}
                  {...getItemProps({
                    type: 'button',
                    role: 'menuitem',
                    onClick: () => select(item),
                  })}
                  width="100%"
                  alignItems="center"
                  borderRadius="secondary"
                  gap="2x"
                  sx={{
                    boxSizing: 'border-box',
                    padding: `10px ${theme.space['3x']}`,
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: theme.text['100'].fontSize,
                    fontWeight: theme.fontWeight.semibold,
                    color: item.destructive ? theme.textColor.error : theme.textColor.primary,
                    ':hover, :focus': { backgroundColor: theme.background.secondary },
                  }}
                >
                  <item.icon width={ICON_SIZE} height={ICON_SIZE} fill="currentColor" />
                  {item.label}
                </Flex>
              ))}
            </Box>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};

export default ActionMenu;
